/**
 * CentralBeadDepot storefront behaviour layered on Horizon.
 * Loaded as a module from layout/theme.liquid.
 *
 * Custom elements:
 *  - <cbd-shipping-bar>     Free-shipping progress that follows Horizon cart updates.
 *  - <cbd-next-ship-day>    Prints the next Monday, Wednesday or Friday ship day.
 *  - <cbd-help-fab>         Floating "need more than what's listed?" helper.
 *  - <cbd-recently-viewed>  Renders Horizon's recently viewed product IDs through the Section Rendering API.
 *  - <cbd-header>           Menu drawer, dropdowns, mobile search and live product suggestions.
 *  - <cbd-collection>       Storefront filters and sorting, updated in place.
 *  - <cbd-product>          Product gallery, option pills, quantity stepper and sticky add bar.
 *  - <cbd-recommendations>  Loads product recommendations and the "Add all to cart" bundle button.
 */
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

/* ---------------- Free shipping progress ---------------- */
class CbdShippingBar extends HTMLElement {
  connectedCallback() {
    this.threshold = Number(this.dataset.thresholdCents) || 0;
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.currency = this.dataset.currency || 'USD';
    this.messageEl = this.querySelector('[data-cbd-message]');
    this.fillEl = this.querySelector('[data-cbd-fill]');
    this.progressEl = this.querySelector('[role="progressbar"]');
    this.onCartUpdate = (event) => {
      const done = () => this.refresh();
      if (event && event.promise) event.promise.then(done, done);
      else done();
    };
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    this.onPageShow = (event) => event.persisted && this.refresh();
    window.addEventListener('pageshow', this.onPageShow);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  async refresh() {
    try {
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      this.render(cart.total_price);
    } catch (error) {
      console.warn('[cbd-shipping-bar] Could not refresh cart total', error);
    }
  }

  /** @param {number} totalCents */
  render(totalCents) {
    if (!this.threshold) return;
    const pct = Math.min(100, (totalCents / this.threshold) * 100);
    let message;
    if (totalCents >= this.threshold) {
      message = this.dataset.msgUnlocked;
    } else if (totalCents === 0) {
      message = this.dataset.msgEmpty;
    } else {
      const remaining = formatMoney(this.threshold - totalCents, this.moneyFormat, this.currency);
      message = (this.dataset.msgProgress || '').replace('[amount]', remaining);
    }
    if (this.messageEl && message) this.messageEl.textContent = message;
    if (this.fillEl) this.fillEl.style.width = `${pct.toFixed(1)}%`;
    if (this.progressEl) this.progressEl.setAttribute('aria-valuenow', String(Math.min(totalCents, this.threshold)));
  }
}

/* ---------------- Next ship day ---------------- */
class CbdNextShipDay extends HTMLElement {
  connectedCallback() {
    const shipDays = (this.dataset.days || '1,3,5').split(',').map(Number);
    const today = new Date();
    for (let i = 1; i <= 7; i += 1) {
      const next = new Date(today);
      next.setDate(today.getDate() + i);
      if (shipDays.includes(next.getDay())) {
        this.textContent = next.toLocaleDateString(document.documentElement.lang || 'en-US', {
          weekday: 'long',
          month: 'short',
          day: 'numeric',
        });
        return;
      }
    }
  }
}

/* ---------------- "Need more?" helper ---------------- */
class CbdHelpFab extends HTMLElement {
  static STORAGE_KEY = 'cbd-help-dismissed';

  connectedCallback() {
    let dismissed = false;
    try {
      dismissed = localStorage.getItem(CbdHelpFab.STORAGE_KEY) === '1';
    } catch (error) {
      dismissed = false;
    }
    if (dismissed && !window.Shopify?.designMode) {
      this.hidden = true;
      return;
    }
    this.hidden = false;
    this.panel = this.querySelector('[data-cbd-panel]');
    this.toggleButton = this.querySelector('[data-cbd-toggle]');
    this.toggleButton?.addEventListener('click', () => this.setOpen(this.panel?.hidden ?? false));
    this.querySelector('[data-cbd-close]')?.addEventListener('click', () => this.setOpen(false));
    this.querySelector('[data-cbd-dismiss]')?.addEventListener('click', () => {
      try {
        localStorage.setItem(CbdHelpFab.STORAGE_KEY, '1');
      } catch (error) {
        /* storage unavailable: hide for this page view only */
      }
      this.hidden = true;
    });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.setOpen(false);
    });
  }

  /** @param {boolean} open */
  setOpen(open) {
    if (!this.panel || !this.toggleButton) return;
    this.panel.hidden = !open;
    this.toggleButton.setAttribute('aria-expanded', String(open));
    if (open) this.panel.querySelector('a, button')?.focus();
  }
}

/* ---------------- Recently viewed ---------------- */
class CbdRecentlyViewed extends HTMLElement {
  async connectedCallback() {
    let ids = [];
    try {
      ids = JSON.parse(localStorage.getItem('viewedProducts') || '[]');
    } catch (error) {
      ids = [];
    }
    const exclude = this.dataset.excludeId;
    ids = ids.filter((id) => String(id) !== String(exclude)).slice(0, Number(this.dataset.limit) || 4);
    if (!ids.length) return;

    const query = ids.map((id) => `id:${id}`).join(' OR ');
    const url = `${root}search?q=${encodeURIComponent(query)}&type=product&section_id=${encodeURIComponent(
      this.dataset.sectionId || ''
    )}`;
    try {
      const html = await (await fetch(url)).text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const results = doc.querySelector('[data-cbd-recent-results]');
      const target = this.querySelector('[data-cbd-recent-results]');
      if (!results || !target || !results.children.length) return;
      target.replaceChildren(...results.childNodes);
      this.hidden = false;
    } catch (error) {
      console.warn('[cbd-recently-viewed] Could not load products', error);
    }
  }
}


/* ---------------- Shared helpers ---------------- */
const debounce = (fn, wait) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
};

/** Section ids of Horizon cart components on the page, so their markup comes back with the add. */
const cartSectionIds = () =>
  [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean)
    .join(',');

/**
 * Adds several variants in one request and tells Horizon's cart drawer, cart icon and
 * other listeners about it, the same way Horizon's own product form does.
 * @param {{id: number, quantity: number}[]} items
 * @param {Element} source
 */
async function addLinesToCart(items, source) {
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
    })
  );
  try {
    const sections = cartSectionIds();
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, ...(sections ? { sections } : {}) }),
    });
    const data = await response.json();
    const cart = await (await fetch(`${root}cart.js`)).json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'cbd-bundle',
        itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
        sections: data.sections,
        didError: Boolean(data.status),
      },
    });
    return !data.status;
  } catch (error) {
    deferred.reject(error);
    return false;
  }
}

/* Keep the header cart count in step with Horizon cart updates */
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const render = (count) =>
    document.querySelectorAll('[data-cbd-cart-count]').forEach((el) => {
      el.textContent = String(count);
    });
  const fallback = () =>
    fetch(`${root}cart.js`)
      .then((r) => r.json())
      .then((cart) => render(cart.item_count))
      .catch(() => {});
  if (event.promise) {
    event.promise
      .then(({ cart, detail }) => {
        const count = cart?.totalQuantity ?? detail?.itemCount;
        if (typeof count === 'number' && cart) render(count);
        else fallback();
      })
      .catch(fallback);
  } else {
    fallback();
  }
});

/* ---------------- Header ---------------- */
class CbdHeader extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section') || this;
    this.setHeight = () => {
      const h = Math.round(this.section.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--cbd-header-h', `${h}px`);
      document.body.style.setProperty('--header-height', `${h}px`);
    };
    this.setHeight();
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(this.setHeight);
      this.resizeObserver.observe(this.section);
    }

    /* Menu drawer */
    this.menu = /** @type {HTMLDialogElement|null} */ (this.querySelector('[data-cbd-menu]'));
    this.menuButton = this.querySelector('[data-cbd-menu-open]');
    this.menuButton?.addEventListener('click', () => {
      this.menu?.showModal();
      this.menuButton?.setAttribute('aria-expanded', 'true');
    });
    this.querySelector('[data-cbd-menu-close]')?.addEventListener('click', () => this.menu?.close());
    this.menu?.addEventListener('close', () => this.menuButton?.setAttribute('aria-expanded', 'false'));
    this.menu?.addEventListener('click', (event) => {
      if (event.target === this.menu) this.menu.close();
    });

    /* Dropdowns: open on hover (desktop) or click */
    this.querySelectorAll('[data-cbd-dropdown-toggle]').forEach((toggle) => {
      const item = toggle.closest('.cbd-nav__item');
      const panel = item?.querySelector('.cbd-dropdown');
      if (!item || !panel) return;
      const set = (open) => {
        panel.hidden = !open;
        toggle.setAttribute('aria-expanded', String(open));
      };
      toggle.addEventListener('click', () => set(panel.hidden));
      item.addEventListener('mouseenter', () => window.matchMedia('(hover: hover)').matches && set(true));
      item.addEventListener('mouseleave', () => window.matchMedia('(hover: hover)').matches && set(false));
      item.addEventListener('focusout', (event) => {
        if (!item.contains(/** @type {Node} */ (event.relatedTarget))) set(false);
      });
      item.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          set(false);
          toggle.focus();
        }
      });
    });

    /* Search */
    this.searchForm = this.querySelector('[data-cbd-search]');
    this.searchInput = /** @type {HTMLInputElement|null} */ (this.querySelector('[data-cbd-search-input]'));
    this.results = /** @type {HTMLElement|null} */ (this.querySelector('[data-cbd-search-results]'));
    this.searchToggle = this.querySelector('[data-cbd-search-toggle]');
    this.searchToggle?.addEventListener('click', () => {
      const open = !this.hasAttribute('data-search-open');
      this.toggleAttribute('data-search-open', open);
      this.searchToggle?.setAttribute('aria-expanded', String(open));
      if (open) this.searchInput?.focus();
      else this.closeResults();
    });
    this.searchInput?.addEventListener('input', debounce(() => this.suggest(), 200));
    this.searchInput?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.searchInput.value = '';
        this.closeResults();
      }
    });
    this.onDocClick = (event) => {
      if (this.searchForm && !this.searchForm.contains(event.target)) this.closeResults();
    };
    document.addEventListener('click', this.onDocClick);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    document.removeEventListener('click', this.onDocClick);
  }

  closeResults() {
    if (this.results) {
      this.results.hidden = true;
      this.results.innerHTML = '';
    }
  }

  async suggest() {
    const q = this.searchInput?.value.trim() || '';
    if (!q || !this.results) {
      this.closeResults();
      return;
    }
    this.abort?.abort();
    this.abort = new AbortController();
    const params = new URLSearchParams({
      q,
      section_id: 'cbd-predictive-search',
      'resources[type]': 'product',
      'resources[limit]': '6',
      'resources[options][unavailable_products]': 'last',
    });
    try {
      const html = await (
        await fetch(`${this.dataset.suggestUrl}?${params}`, { signal: this.abort.signal })
      ).text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('[data-cbd-suggest]');
      if (!content || !content.innerHTML.trim()) {
        this.closeResults();
        return;
      }
      this.results.innerHTML = content.innerHTML;
      this.results.hidden = false;
    } catch (error) {
      if (error?.name !== 'AbortError') this.closeResults();
    }
  }
}

/* ---------------- Collection filters ---------------- */
class CbdCollection extends HTMLElement {
  connectedCallback() {
    this.form = /** @type {HTMLFormElement} */ (this.querySelector('[data-cbd-filter-form]'));
    this.dialog = /** @type {HTMLDialogElement|null} */ (this.querySelector('[data-cbd-filters]'));
    this.mobile = window.matchMedia('(max-width: 749px)');

    this.addEventListener('click', (event) => {
      const target = /** @type {Element} */ (event.target);
      if (target.closest('[data-cbd-filter-open]')) this.dialog?.showModal();
      if (target.closest('[data-cbd-filter-close]')) this.dialog?.close();
      if (event.target === this.dialog) this.dialog?.close();
      const link = target.closest('a[data-cbd-link], a[data-cbd-clear]');
      if (link instanceof HTMLAnchorElement) {
        event.preventDefault();
        this.load(link.href);
      }
    });

    this.addEventListener('change', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      if (target.matches('[data-cbd-sort-select], [data-cbd-sort-radio]')) {
        const sortInput = /** @type {HTMLInputElement} */ (this.querySelector('[data-cbd-sort-input]'));
        sortInput.value = /** @type {HTMLInputElement} */ (target).value;
      }
      if (target.closest('form') === this.form) this.submit();
    });

    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.submit();
    });

    window.addEventListener('popstate', () => this.load(window.location.href, false));
  }

  submit() {
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(this.form)) {
      if (key === 'cbd_sort_mobile' || value === '') continue;
      params.append(key, String(value));
    }
    this.load(`${this.dataset.url}?${params.toString()}`);
  }

  /** @param {string} url @param {boolean} [push] */
  async load(url, push = true) {
    const target = new URL(url, window.location.origin);
    const fetchUrl = new URL(target);
    fetchUrl.searchParams.set('section_id', this.dataset.sectionId || '');
    this.setAttribute('aria-busy', 'true');
    try {
      const html = await (await fetch(fetchUrl)).text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const focusedName = document.activeElement?.getAttribute('name');
      const focusedValue = /** @type {HTMLInputElement} */ (document.activeElement)?.value;
      for (const part of ['mobilebar', 'facets', 'show', 'main']) {
        const next = doc.querySelector(`[data-cbd-part="${part}"]`);
        const current = this.querySelector(`[data-cbd-part="${part}"]`);
        if (next && current) current.innerHTML = next.innerHTML;
      }
      const sortInput = /** @type {HTMLInputElement|null} */ (this.querySelector('[data-cbd-sort-input]'));
      const nextSort = /** @type {HTMLInputElement|null} */ (doc.querySelector('[data-cbd-sort-input]'));
      if (sortInput && nextSort) sortInput.value = nextSort.value;
      if (focusedName) {
        const again = [...this.querySelectorAll(`[name="${CSS.escape(focusedName)}"]`)].find(
          (el) => /** @type {HTMLInputElement} */ (el).value === focusedValue
        );
        /** @type {HTMLElement|undefined} */ (again)?.focus({ preventScroll: true });
      }
      if (push) history.pushState({}, '', target.pathname + target.search);
    } catch (error) {
      window.location.href = target.toString();
    } finally {
      this.removeAttribute('aria-busy');
    }
  }
}

/* ---------------- Product page ---------------- */
class CbdProduct extends HTMLElement {
  connectedCallback() {
    try {
      this.variants = JSON.parse(this.querySelector('[data-cbd-variants]')?.textContent || '[]');
      this.strings = JSON.parse(this.querySelector('[data-cbd-strings]')?.textContent || '{}');
    } catch (error) {
      this.variants = [];
      this.strings = {};
    }
    this.lowAt = Number(this.dataset.lowAt) || 0;
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.currency = this.dataset.currency || 'USD';

    /* Gallery */
    this.slides = [...this.querySelectorAll('[data-cbd-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-cbd-thumb]')];
    this.counter = this.querySelector('[data-cbd-counter]');
    const gallery = /** @type {HTMLElement|null} */ (this.querySelector('[data-cbd-gallery]'));
    this.index = Number(gallery?.dataset.index) || 0;
    this.querySelector('[data-cbd-prev]')?.addEventListener('click', () => this.show(this.index - 1));
    this.querySelector('[data-cbd-next]')?.addEventListener('click', () => this.show(this.index + 1));
    this.thumbs.forEach((thumb) =>
      thumb.addEventListener('click', () => this.show(Number(/** @type {HTMLElement} */ (thumb).dataset.cbdThumb)))
    );
    gallery?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.show(this.index - 1);
      if (event.key === 'ArrowRight') this.show(this.index + 1);
    });

    /* Quantity */
    this.qty = /** @type {HTMLInputElement|null} */ (this.querySelector('[data-cbd-qty]'));
    this.querySelector('[data-cbd-qty-dec]')?.addEventListener('click', () => this.setQty(this.getQty() - 1));
    this.querySelector('[data-cbd-qty-inc]')?.addEventListener('click', () => this.setQty(this.getQty() + 1));
    this.qty?.addEventListener('change', () => this.setQty(this.getQty()));

    /* Options */
    this.querySelectorAll('[data-cbd-option] input').forEach((input) =>
      input.addEventListener('change', () => this.onOptionChange())
    );
    this.variant = this.currentVariant();
    this.markUnavailable();

    /* Sticky add bar (mobile) */
    this.sticky = /** @type {HTMLElement|null} */ (this.querySelector('[data-cbd-sticky]'));
    const mainButton = this.querySelector('.cbd-pdp__atc');
    if (this.sticky && mainButton && window.IntersectionObserver) {
      this.stickyObserver = new IntersectionObserver(([entry]) => {
        if (this.sticky) this.sticky.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
      });
      this.stickyObserver.observe(mainButton);
    }
    this.querySelector('[data-cbd-sticky-add]')?.addEventListener('click', () => {
      /** @type {HTMLButtonElement|null} */ (this.querySelector('.cbd-pdp__atc'))?.click();
    });
  }

  disconnectedCallback() {
    this.stickyObserver?.disconnect();
  }

  money(cents) {
    return formatMoney(cents, this.moneyFormat, this.currency);
  }

  show(index) {
    if (!this.slides.length) return;
    this.index = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, i) => (/** @type {HTMLElement} */ (slide).hidden = i !== this.index));
    this.thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === this.index));
    this.thumbs[this.index]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    if (this.counter) this.counter.textContent = `${this.index + 1} / ${this.slides.length}`;
  }

  getQty() {
    return Math.max(1, parseInt(this.qty?.value || '1', 10) || 1);
  }

  setQty(value) {
    let next = Math.max(1, value);
    const max = this.variant?.tracked ? this.variant.inventory : null;
    if (max != null && max > 0) next = Math.min(next, max);
    if (this.qty) this.qty.value = String(next);
    this.updateButton();
  }

  selectedOptions() {
    return [...this.querySelectorAll('[data-cbd-option]')].map(
      (fieldset) => /** @type {HTMLInputElement|null} */ (fieldset.querySelector('input:checked'))?.value
    );
  }

  currentVariant() {
    const idInput = /** @type {HTMLInputElement|null} */ (this.querySelector('[data-cbd-variant-id]'));
    return this.variants.find((v) => String(v.id) === idInput?.value) || this.variants[0];
  }

  onOptionChange() {
    const selected = this.selectedOptions();
    this.querySelectorAll('[data-cbd-option]').forEach((fieldset, i) => {
      const label = fieldset.querySelector('[data-cbd-option-value]');
      if (label) label.textContent = selected[i] || '';
    });
    const match = this.variants.find((v) => v.options.every((opt, i) => opt === selected[i]));
    this.variant = match || null;
    const idInput = /** @type {HTMLInputElement|null} */ (this.querySelector('[data-cbd-variant-id]'));
    if (match && idInput) {
      idInput.value = String(match.id);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(match.id));
      history.replaceState({}, '', url.toString());
      if (match.media >= 0) this.show(match.media);
    }
    this.markUnavailable();
    this.setQty(this.getQty());
    this.updateVariantUI();
  }

  markUnavailable() {
    const selected = this.selectedOptions();
    this.querySelectorAll('[data-cbd-option]').forEach((fieldset, optionIndex) => {
      fieldset.querySelectorAll('.cbd-pdp__pill').forEach((pill) => {
        const input = /** @type {HTMLInputElement} */ (pill.querySelector('input'));
        const candidate = [...selected];
        candidate[optionIndex] = input.value;
        const available = this.variants.some(
          (v) => v.available && v.options.every((opt, i) => opt === candidate[i])
        );
        pill.classList.toggle('is-unavailable', !available);
      });
    });
  }

  updateVariantUI() {
    const v = this.variant;
    const price = this.querySelector('[data-cbd-price]');
    const compare = /** @type {HTMLElement|null} */ (this.querySelector('[data-cbd-compare]'));
    const stickyPrice = this.querySelector('[data-cbd-sticky-price]');
    const stickyCompare = /** @type {HTMLElement|null} */ (this.querySelector('[data-cbd-sticky-compare]'));
    const save = /** @type {HTMLElement|null} */ (this.querySelector('[data-cbd-save]'));
    if (v) {
      const onSale = v.compare > v.price;
      if (price) price.textContent = this.money(v.price);
      if (stickyPrice) stickyPrice.textContent = this.money(v.price);
      for (const el of [compare, stickyCompare]) {
        if (!el) continue;
        el.hidden = !onSale;
        el.textContent = onSale ? this.money(v.compare) : '';
      }
      if (save) {
        save.hidden = !onSale;
        if (onSale) {
          const percent = Math.round(((v.compare - v.price) * 100) / v.compare);
          save.textContent = (this.strings.savePercent || '').replace('[percent]', String(percent));
        }
      }
    }
    const stock = this.querySelector('[data-cbd-stock]');
    const stockText = this.querySelector('[data-cbd-stock-text]');
    let text = this.strings.unavailable;
    let low = true;
    if (v && !v.available) text = this.strings.soldOut;
    else if (v && v.tracked && v.inventory != null) {
      low = v.inventory <= this.lowAt;
      text = (low ? this.strings.onlyLeft : this.strings.inStockCount).replace('999999', String(v.inventory));
    } else if (v) {
      text = this.strings.inStock;
      low = false;
    }
    if (stockText) stockText.textContent = text;
    stock?.classList.toggle('is-low', low);
    stock?.classList.toggle('is-ok', !low);
    this.updateButton();
  }

  updateButton() {
    const v = this.variant;
    const button = /** @type {HTMLButtonElement|null} */ (this.querySelector('.cbd-pdp__atc'));
    const label = this.querySelector('[data-cbd-atc-label]');
    const stickyButton = /** @type {HTMLButtonElement|null} */ (this.querySelector('[data-cbd-sticky-add]'));
    const canAdd = Boolean(v && v.available);
    if (button) button.disabled = !canAdd;
    if (stickyButton) stickyButton.disabled = !canAdd;
    if (label) {
      label.textContent = !v
        ? this.strings.unavailable
        : !v.available
          ? this.strings.soldOut
          : this.strings.addToCart.replace('[price]', this.money(v.price * this.getQty()));
    }
  }
}

/* ---------------- Recommendations ---------------- */
class CbdRecommendations extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      const button = /** @type {Element} */ (event.target).closest('[data-cbd-add-all]');
      if (!(button instanceof HTMLButtonElement)) return;
      const ids = (button.dataset.cbdAddAll || '').split(',').filter(Boolean);
      button.disabled = true;
      addLinesToCart(
        ids.map((id) => ({ id: Number(id), quantity: 1 })),
        button
      ).finally(() => {
        button.disabled = false;
      });
    });
    if (this.children.length && !this.hidden) return;
    this.load();
  }

  async load() {
    if (!this.dataset.url) return;
    try {
      const html = await (await fetch(this.dataset.url)).text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('cbd-recommendations');
      if (next && next.innerHTML.trim()) {
        this.innerHTML = next.innerHTML;
        this.hidden = false;
      }
    } catch (error) {
      /* recommendations are optional */
    }
  }
}

const define = (name, ctor) => {
  if (!customElements.get(name)) customElements.define(name, ctor);
};
define('cbd-shipping-bar', CbdShippingBar);
define('cbd-next-ship-day', CbdNextShipDay);
define('cbd-help-fab', CbdHelpFab);
define('cbd-recently-viewed', CbdRecentlyViewed);
define('cbd-header', CbdHeader);
define('cbd-collection', CbdCollection);
define('cbd-product', CbdProduct);
define('cbd-recommendations', CbdRecommendations);
